// src/api.js
import axios from "axios";
import {
  getAccessToken,
  getRefreshToken,
  saveTokens,
  clearTokens,
  isTokenNearlyExpired,
} from "./auth";

// ★ 브라우저/HTTPS 여부 판단
const isBrowser = typeof window !== "undefined";

// 1) baseURL은 항상 '/api/' 로 끝나도록 보정
let rawBase =
  process.env.REACT_APP_API_BASE_URL ||
  (isBrowser ? window.location.origin : "http://127.0.0.1:8000");

// ★ HTTPS 페이지에서 http API 감지 시 https로 보정 (Mixed Content 방지)
if (
  isBrowser &&
  window.location.protocol === "https:" &&
  /^http:\/\//i.test(rawBase)
) {
  console.warn(
    "[api.js] HTTPS 페이지에서 HTTP API 감지 → https로 보정:",
    rawBase
  );
  rawBase = rawBase.replace(/^http:\/\//i, "https://");
}

// ✅ 환경에 따라 자동으로 /api/ 붙일지 여부 결정
const USE_API_PREFIX = true; // Django가 /api/를 사용하는 버전이므로 true로 변경
const apiBase = USE_API_PREFIX
  ? `${rawBase.replace(/\/+$/, "")}/api/`
  : `${rawBase.replace(/\/+$/, "")}/`;

// 메인 인스턴스
const api = axios.create({
  baseURL: apiBase,
  timeout: 10000,
  headers: { Accept: "application/json" },
});

// 🔸 인터셉터에 휘말리지 않는 '깨끗한' 인스턴스 (refresh 용)
const authless = axios.create({
  baseURL: apiBase,
  timeout: 10000,
  headers: { Accept: "application/json" },
});

console.log("API baseURL =>", api.defaults.baseURL);

// 도우미: auth 엔드포인트 여부
// - 로그인/리프레시에 Authorization 헤더가 실수로 붙지 않도록 제외
const isAuthEndpoint = (url = "") => {
  const u = String(url || "").replace(apiBase, ""); // 혹시 절대경로로 들어와도 대비
  return (
    /^\/?token\/(obtain|refresh|verify)?/i.test(u) || // /token/*
    /^\/?auth\/login\/?$/i.test(u) || // /auth/login/
    /^\/?auth\/refresh\/?$/i.test(u) || // /auth/refresh/
    /^\/?auth\/jwt\/(create|refresh|verify)\/?$/i.test(u) // /auth/jwt/*
  );
};

/* ==============================
✅ 디버깅: 전역 요청/응답 로깅 추가
============================== */
api.interceptors.request.use(
  (config) => {
    try {
      const absUrl =
        (config.baseURL ? config.baseURL.replace(/\/+$/, "") + "/" : "") +
        String(config.url || "").replace(/^\/+/, "");
      console.log("[API:REQUEST]", {
        url: absUrl,
        method: config.method,
        headers: config.headers,
        params: config.params,
        data: config.data,
      });
    } catch (_) {}
    return config;
  },
  (error) => {
    console.error("[API:REQUEST_ERROR]", error);
    return Promise.reject(error);
  }
);

/* ==========================================
요청 인터셉터 (토큰 부착 + 선제 리프레시 보강)
========================================== */
api.interceptors.request.use(async (config) => {
  const access = getAccessToken();
  const refresh = getRefreshToken();

  // 로그인/리프레시 요청에는 토큰 안 붙임
  if (isAuthEndpoint(config.url)) {
    if (config.headers && "Authorization" in config.headers) {
      delete config.headers.Authorization;
    }
    return config;
  }

  // ★★ 보강 1) access 토큰이 '없고', refresh 는 있을 때 → 선제 갱신 후 부착
  if (!access && refresh) {
    try {
      const res = await authless.post("token/refresh/", { refresh });
      const newAccess = res.data?.access;
      const nextRefresh = res.data?.refresh || refresh;
      if (newAccess) {
        saveTokens(newAccess, nextRefresh);
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${newAccess}`;
        console.log("[API] pre-attach via refresh (no access → got new access)");
        return config;
      }
    } catch (e) {
      console.warn("[API] pre-refresh (no access) FAIL:", e?.response?.data || e.message);
      clearTokens(); // 그대로 진행 → 401이면 응답 인터셉터에서 다시 처리
    }
  }

  // ★★ 보강 2) access 가 있는데 만료 임박이면 선제 갱신
  if (access && refresh && isTokenNearlyExpired(access, 60)) {
    try {
      const res = await authless.post("token/refresh/", { refresh });
      const newAccess = res.data.access;
      const nextRefresh = res.data.refresh || refresh;
      saveTokens(newAccess, nextRefresh);
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${newAccess}`;
      console.log("[API] pre-refresh OK (nearly expired)");
      return config;
    } catch (e) {
      console.warn("[API] pre-refresh FAIL:", e?.response?.data || e.message);
      clearTokens(); // 그대로 진행 → 401이면 응답 인터셉터에서 처리
    }
  }

  // 평소에는 액세스만 붙임
  if (access) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${access}`;
  }
  return config;
});

/* ==========================================
응답 인터셉터: 401 → 1회 자동 갱신 후 재시도
========================================== */
api.interceptors.response.use(
  (res) => {
    try {
      console.log("[API:RESPONSE]", {
        url: res.config?.url,
        status: res.status,
        data: res.data,
      });
    } catch (_) {}
    return res;
  },
  async (error) => {
    const { config, response } = error;
    if (!response) {
      console.error("[API:NETWORK_ERROR]", error.message);
      return Promise.reject(error);
    }

    // auth 엔드포인트는 건드리지 않음
    if (isAuthEndpoint(config.url)) {
      console.warn("[API] auth endpoint error:", response.status, response.data);
      return Promise.reject(error);
    }

    if (response.status === 401 && !config._retry) {
      config._retry = true;
      const refresh = getRefreshToken();
      if (refresh) {
        try {
          const res = await authless.post("token/refresh/", { refresh });
          const newAccess = res.data.access;
          const nextRefresh = res.data.refresh || refresh;
          saveTokens(newAccess, nextRefresh);
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${newAccess}`;
          console.log("[API] 401-refresh OK → retry");
          return api(config);
        } catch (e) {
          console.warn("[API] 401-refresh FAIL:", e?.response?.data || e.message);
          clearTokens();
        }
      }
    }

    console.error("[API:RESPONSE_ERROR]", {
      url: config?.url,
      status: response.status,
      data: response.data,
    });
    return Promise.reject(error);
  }
);

/* ===========================
✅ 로그인 연장 전용 함수
=========================== */
export async function extendLogin() {
  const refresh = getRefreshToken();
  if (!refresh) {
    const err = new Error("연장 불가: refresh 토큰이 없습니다. 다시 로그인하세요.");
    console.error("[extendLogin]", err.message);
    throw err;
  }

  try {
    const res = await authless.post("token/refresh/", { refresh });
    const newAccess = res.data?.access;
    const nextRefresh = res.data?.refresh || refresh;
    if (!newAccess) throw new Error("서버가 access 토큰을 반환하지 않았습니다.");

    // 저장
    saveTokens(newAccess, nextRefresh);

    // 만료시각(타이머) 동기화: refresh 토큰 exp를 디코드해서 저장
    const decode = (jwt) => {
      try {
        return JSON.parse(atob(jwt.split(".")[1]));
      } catch {
        return null;
      }
    };

    const rPayload = decode(nextRefresh);
    if (rPayload?.exp) {
      const iso = new Date(rPayload.exp * 1000).toISOString();
      localStorage.setItem("loginDeadline", iso);
      console.log("[extendLogin] refresh exp →", iso);
    } else {
      const iso = new Date(Date.now() + 12 * 60 * 60 * 1000).toISOString();
      localStorage.setItem("loginDeadline", iso);
      console.log("[extendLogin] fallback deadline(+12h) →", iso);
    }

    console.log("[extendLogin] OK: tokens saved & deadline updated");
    return true;
  } catch (e) {
    console.error("[extendLogin] FAIL:", e?.response?.data || e.message);
    clearTokens();
    throw e;
  }
}

export default api;
